import {
  Button,
  Divider,
  FormSection,
  Grid,
  Loader,
  styled,
  Switch,
} from "@monolith-forensics/monolith-ui";
import { useForm, zodResolver } from "@mantine/form";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Link } from "react-router-dom";
import z from "zod";

import { mergeInquiry } from "@/api/inquiries";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import {
  FormDescription,
  FormTitle,
  FormWrapper,
} from "@/components/Forms/components/MonolithFormComponents";
import {
  Case,
  CaseSelector,
  CasesQuery,
  CocIntakeForm,
  Signature,
  EvidenceSelector,
} from "@/Features";

export type MergeInquiryFormData = {
  case_id: number | undefined;
  case?: { label: string; value: number; data: Case };
  inquiry_id: number;
  import_evidence: boolean;
  import_people: boolean;
  import_docs: boolean;
  include_coc: boolean;
  link_evidence: boolean;
  coc?: {
    timestamp: string; // date/time of intake
    custody_from: string; // name of person that provided the items
    signature: {
      custody_to_sig: Signature; // signature of the person that received the items
      custody_from_sig: Signature; // signature of the person that provided the items
    }; // signature of the person that provided the items
    location: { location_id: number }; // location id of the location that received the items
    location_received: string; // location name of the location that received the items
    reason: string; // reason for the intake
  };
  evidence_number_import: boolean;
  case_evidence_links?:
    | Map<string, string>
    | { request_item_id: string; case_item_id: string }[]; // inquiry evidence item_id -> case evidence uuid
  inquiry_evidence?: {
    item_id: string;
    uuid: string;
    evidence_number: string;
    evidence_type: string;
    make: string;
    model_number: string;
    description: string;
  }[];
};

export const MergeInquiryFormDataSchema = z.object({
  case_id: z
    .number({ message: "Case is required" })
    .min(1, { message: "Case is required" }),
  import_evidence: z.boolean(),
  import_people: z.boolean(),
  import_docs: z.boolean(),
  include_coc: z.boolean(),
  coc: z
    .object({
      timestamp: z
        .string({ message: "Timestamp is required" })
        .min(1, { message: "Timestamp is required" }),
      custody_from: z
        .string({ message: "Received from is required" })
        .min(1, { message: "Received from is required" }),
      custody_from_sig: z.any(),
      custody_to_sig: z.any(),
      location: z
        .object({
          location_id: z
            .number({ message: "Received by is required" })
            .min(1, { message: "Received by is required" }),
        })
        .catchall(z.any()),
      location_received: z.string().optional(),
      reason: z.string().optional(),
    })
    .optional(),
  evidence_number_import: z.boolean(),
  link_evidence: z.boolean(),
  case_evidence_links: z.map(z.string(), z.string()),
});

const StyledCaseComponent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  justify-content: space-between;
  align-items: center;
  gap: 5px;
  padding: 10px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 6px;
  cursor: pointer;

  transition: all 0.2s ease-in-out;

  .case-name {
    font-size: 12px;
    font-weight: bold;
    color: ${({ theme }) => theme.palette.primary.main};
    max-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
      text-decoration: underline;
    }
  }

  .case-number {
    font-size: 12px;
  }

  .client-name {
    font-size: 12px;
    font-weight: bold;
    color: ${({ theme }) => theme.palette.text.primary};
  }

  .client-organization {
    font-size: 12px;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .case-type {
    text-align: right;
  }

  &:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
  }

  &[data-selected="true"] {
    border-color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const RelatedCaseComponent: React.FC<{
  forensicCase: Case;
  onClick?: (d: Case) => void;
  selected?: boolean;
}> = ({ forensicCase, onClick, selected }) => {
  return (
    <StyledCaseComponent
      onClick={() => onClick?.(forensicCase)}
      data-selected={selected}
    >
      <div>
        <Link
          to={`/cases/${forensicCase.case_id}/overview`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="case-name">{forensicCase.case_ref}</div>
        </Link>
        <div className="case-number">{forensicCase.case_number}</div>
      </div>
      <div>
        <div className="client-name">
          {forensicCase.client_name || "Unknown Client"}
        </div>
        <div className="client-organization">
          {forensicCase.client_organization || "Unknown Organization"}
        </div>
      </div>
      <div className="case-type">
        {forensicCase.case_type || "Unknown Case Type"}
      </div>
    </StyledCaseComponent>
  );
};

export const MergeInquiryForm: React.FC<{
  inquiry: { inquiry_id: number; inquiry_case_num: string };
  onSubmit: (data: MergeInquiryFormData) => void;
  onCancel: () => void;
  defaultFormData?: Partial<MergeInquiryFormData>;
}> = ({ defaultFormData, onSubmit, onCancel, inquiry }) => {
  const [submitting, setSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      case_id: defaultFormData?.case_id,
      case: defaultFormData?.case,
      inquiry_id: inquiry?.inquiry_id,
      import_evidence: defaultFormData?.import_evidence ?? true,
      import_people: defaultFormData?.import_people ?? true,
      import_docs: defaultFormData?.import_docs ?? true,
      include_coc: defaultFormData?.include_coc || false,
      coc: defaultFormData?.coc,
      evidence_number_import: defaultFormData?.evidence_number_import || false,
      case_evidence_links: new Map(),
      link_evidence: defaultFormData?.link_evidence || false,
    },
    validate: zodResolver(MergeInquiryFormDataSchema),
  });

  const evidenceLinkForm = useForm({
    mode: "uncontrolled",
  });

  const formData = form.getValues();

  // Fetch possibly related cases using the client ref name
  const { data: cases, isLoading: searchingCases } = CasesQuery.useGetCases(
    {
      search: inquiry.inquiry_case_num,
      data_only: false,
    },
    { enabled: !!inquiry.inquiry_case_num }
  );

  const relatedCases = cases?.data || [];

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const validateResult = form.validate();

    if (validateResult.hasErrors) {
      enqueueSnackbar(
        `Please complete all required fields. ${JSON.stringify(
          Object.values(validateResult.errors)
        )}`,
        {
          variant: "error",
        }
      );
      setSubmitting(false);
      return;
    }

    const formData = form.getValues();

    if (formData.case_evidence_links) {
      const entries = formData.case_evidence_links.entries();
      formData.case_evidence_links = Array.from(entries).map(
        ([request_item_id, case_item_id]) => ({
          request_item_id,
          case_item_id,
        })
      ) as any;
    }

    delete formData.case;

    await mergeInquiry(inquiry.inquiry_id, formData);

    onSubmit?.({ ...formData, inquiry_id: inquiry.inquiry_id });
    setSubmitting(false);
  };

  const handleCaseSelect = (c: Case) => {
    form.setFieldValue("case", {
      label: `${c.case_number}${c.case_ref ? ` ${c.case_ref}` : ""}`,
      value: c.case_id,
      data: c,
    });
    form.setFieldValue("case_id", c.case_id);

    // Clear the case evidence links
    form.setFieldValue("case_evidence_links", new Map());

    // Clear the evidence link form
    evidenceLinkForm.reset();
  };

  const mappedCaseEvidence = Array.from(
    formData?.case_evidence_links?.values?.() || []
  );

  return (
    <FormWrapper width={600}>
      <div>
        <FormTitle>Merge Inquiry</FormTitle>
        <FormDescription>
          Merge the data from this inquiry into a case that already exists.
        </FormDescription>
      </div>
      <FormSection allowCollapse={false} title="Case Selection">
        <CaseSelector
          clearable={true}
          required={true}
          disabled={submitting}
          openOnFocus={false}
          {...form.getInputProps("case")}
          key={form.key("case")}
          onChange={(value, option) => {
            form.setFieldValue("case", option);
            form.setFieldValue("case_id", option?.value);
            evidenceLinkForm.reset();
          }}
        />
        <div style={{ marginTop: 10 }}>
          {searchingCases && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div>Searching cases...</div>
              <Loader size={16} />
            </div>
          )}
          {!searchingCases && (
            <>
              <FormDescription>
                Here are some cases that may be related to this inquiry - click
                on a case to select it for merging.
              </FormDescription>
              {relatedCases.length === 0 && (
                <div style={{ marginTop: 10 }}>
                  Could not find any related cases...try searching in the case
                  selector above.
                </div>
              )}
              {relatedCases.length > 0 && (
                <div
                  style={{
                    marginTop: 10,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    maxHeight: 300,
                    overflowY: "auto",
                  }}
                >
                  {relatedCases.map((c) => (
                    <RelatedCaseComponent
                      key={c.case_id}
                      forensicCase={c}
                      onClick={handleCaseSelect}
                      selected={formData.case?.value === c.case_id}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </FormSection>
      <FormSection allowCollapse={false} title="Merge Options">
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            gap: 20,
          }}
        >
          <Switch
            defaultValue={formData.import_evidence}
            onChange={(value) => {
              form.setFieldValue("import_evidence", value);
            }}
            label="Import Evidence"
            description="Import evidence from the inquiry into the case"
            size="xs"
            disabled={submitting}
          />
          <Switch
            defaultValue={formData.import_people}
            onChange={(value) => {
              form.setFieldValue("import_people", value);
            }}
            label="Import People"
            description="Import people from the inquiry into the case"
            disabled={submitting}
          />
          <Switch
            defaultValue={formData.import_docs}
            onChange={(value) => {
              form.setFieldValue("import_docs", value);
            }}
            label="Import Documents"
            description="Import documents from the inquiry into the case"
            disabled={submitting}
          />
        </div>
      </FormSection>
      {formData.import_evidence && (
        <FormSection
          defaultOpen={false}
          allowCollapse={true}
          title="Evidence Options"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div>
                <div>
                  <h4>Evidence Numbers</h4>
                  <FormDescription style={{ fontSize: 12, marginBottom: 10 }}>
                    Import evidence numbers from the inquiry into the case,
                    otherwise auto-generate new evidence numbers.
                  </FormDescription>
                </div>
                <Switch
                  defaultValue={formData.evidence_number_import}
                  onChange={(value) => {
                    form.setFieldValue("evidence_number_import", value);
                  }}
                  label="Import Evidence Numbers"
                  description="Import evidence numbers from the inquiry into the case, otherwise auto-generate new evidence numbers."
                  disabled={submitting}
                />
              </div>
              <div>
                <div>
                  <h4>Link Evidence</h4>
                  <FormDescription style={{ fontSize: 12, marginBottom: 10 }}>
                    Link evidence in the inquiry to evidence items that already
                    exist in the case.
                  </FormDescription>
                </div>
                <Switch
                  defaultValue={formData.link_evidence}
                  onChange={(value) => {
                    form.setFieldValue("link_evidence", value);
                  }}
                  label="Link Evidence"
                  description="Link evidence in the inquiry to evidence items that already exist in the case."
                  disabled={submitting}
                />
              </div>
              <div>
                <div>
                  <h4>Chain of Custody</h4>
                  <FormDescription style={{ fontSize: 12, marginBottom: 10 }}>
                    Record the initial chain of custody for the evidence items
                    that will be merged into the case.
                  </FormDescription>
                </div>
                <Switch
                  defaultValue={formData.include_coc}
                  onChange={(value) => {
                    form.setFieldValue("include_coc", value);
                  }}
                  label="Include Chain of Custody"
                  description="Include the chain of custody in the merge."
                  disabled={submitting}
                />
              </div>
            </div>
            {formData.link_evidence && (
              <>
                <Divider />
                <div>
                  <h4>Link Evidence</h4>
                  <FormDescription style={{ fontSize: 12, marginBottom: 10 }}>
                    Link evidence in the inquiry to evidence items that already
                    exist in the case.
                  </FormDescription>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 15,
                      marginTop: 15,
                    }}
                  >
                    {defaultFormData?.inquiry_evidence?.length === 0 && (
                      <div>This inquiry has no evidence to link.</div>
                    )}
                    {defaultFormData?.inquiry_evidence?.map((evidence) => (
                      <div key={evidence.item_id}>
                        <Grid col={2}>
                          <div>
                            {[
                              evidence.evidence_number,
                              evidence.evidence_type,
                            ].join(" | ")}
                          </div>
                          <EvidenceSelector
                            disabled={!formData.case || submitting}
                            size="sm"
                            label=""
                            placeholder="Select evidence item..."
                            description="Select an evidence item to acquire."
                            valueKey="uuid"
                            clearable
                            openOnFocus={false}
                            query={{ case_id: formData.case_id }}
                            disabledValues={mappedCaseEvidence}
                            key={evidenceLinkForm.key(evidence.item_id)}
                            {...evidenceLinkForm.getInputProps(
                              evidence.item_id
                            )}
                            onChange={(value) => {
                              evidenceLinkForm.setFieldValue(
                                evidence.item_id,
                                value
                              );
                              if (!value) {
                                const updatedMap = new Map(
                                  formData.case_evidence_links
                                );
                                updatedMap.delete(evidence.item_id);
                                form.setFieldValue(
                                  "case_evidence_links",
                                  updatedMap
                                );
                                return;
                              }
                              form.setFieldValue(
                                "case_evidence_links",
                                new Map(formData.case_evidence_links).set(
                                  evidence.item_id,
                                  value
                                )
                              );
                            }}
                          />
                        </Grid>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {formData.include_coc && (
              <>
                <Divider />
                <div>
                  <div>
                    <h4>Handle Chain of Custody</h4>
                    <FormDescription style={{ fontSize: 12, marginBottom: 10 }}>
                      Create the initial chain of custody record for the
                      evidence.
                    </FormDescription>
                  </div>
                  <CocIntakeForm
                    showButtonMenu={false}
                    disabled={submitting}
                    onChange={(values) => {
                      form.setFieldValue("coc", {
                        timestamp: values.timestamp,
                        signature: {
                          custody_from_sig: values.custody_from_sig,
                          custody_to_sig: values.custody_to_sig,
                        },
                        custody_from: values.custody_from,
                        location: values.custody_to as unknown as {
                          location_id: number;
                        },
                        location_received: values.location_received,
                        reason: values.reason,
                      });
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </FormSection>
      )}
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          {"Cancel"}
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          loading={submitting}
        >
          {"Merge Inquiry"}
        </Button>
      </ButtonMenu>
    </FormWrapper>
  );
};
